import React, { forwardRef, useImperativeHandle, useState, useEffect, useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Tooltip, DatePicker, Popconfirm, Checkbox, ConfigProvider, Upload, message } from 'antd'
import {
  FileTextOutlined,
  DeleteOutlined,
  SearchOutlined,
  FilterOutlined
} from '@ant-design/icons'

import Button from '../../../components/common/button/button'
import Input from '../../../components/common/input/input'
import Typography from '../../../components/common/typography/typography'
import Table from '../../../components/common/table/table'
import Spinner from '../../../components/common/spinner/spinner'
import AttachmentModal from './attachmentModal/attachmentModal'

import {
  fetchTransactions,
  deleteTransaction,
  fetchTransactionAttachments,
  handleAttachments,
  generatePresignedUrl
} from '../../../api/transactionsApiService/transactionsApiUtils'

import {
  formatCurrency,
  getColumnSearchProps,
  getCustomFilterWithSearch,
  getDateFilterProps,
  getResponsiveTableProps,
  getUniqueValues,
  getAttachmentS3Path
} from './transactionUtils'

import './transactions.scss'
import { useMsal } from '@azure/msal-react'

dayjs.extend(utc)
const { Text } = Typography
const { RangePicker } = DatePicker

const EMPTY_TOTALS = { cashIn: 0, cashOut: 0, systemCOH: 0, openingBalance: 0 }
const EMPTY_FILTERED_TOTALS = { cashIn: 0, cashOut: 0, runningBalance: 0 }

const Transactions = forwardRef((props, ref) => {
  const {
    visibleColumns = [],
    selectedDate,
    onBalanceUpdate,
    onTransactionDeleted,
    onDataLoaded,
    transactionSubmitted,
    onTransactionRefreshed,
    onTransactionsLoaded
  } = props

  const { instance } = useMsal()

  const [transactions, setTransactions] = useState([])
  const [isAttachmentModalVisible, setIsAttachmentModalVisible] = useState(false)
  const [currentAttachment, setCurrentAttachment] = useState(null)
  const [loading, setLoading] = useState(false)
  const [filteredTransactions, setFilteredTransactions] = useState([])
  const [fileList, setFileList] = useState([])
  const [isTablet, setIsTablet] = useState(false)
  const [actionLoading, setActionLoading] = useState({
    delete: null,
    deleteAttachment: {},
    uploadAttachment: false,
    previewAttachment: {},
    downloadAttachment: {}
  })
  const [expenseDepositTypes, setExpenseDepositTypes] = useState([])
  const [currentUser, setCurrentUser] = useState(null)
  const [creatorNames, setCreatorNames] = useState([])
  const [tableFilters, setTableFilters] = useState({})
  const [apiTotals, setApiTotals] = useState(EMPTY_TOTALS)
  const [filteredTotals, setFilteredTotals] = useState(EMPTY_FILTERED_TOTALS)

  const isCashInVisible = visibleColumns.includes('cashIn')
  const isRunningBalanceVisible = visibleColumns.includes('runningBalance')
  const isCurrentDatePrevMonth = selectedDate.isBefore(dayjs(), 'month')

  const totalTransactions = useMemo(() => ({
    cashIn: apiTotals.cashIn,
    cashOut: apiTotals.cashOut,
    runningBalance: apiTotals.systemCOH
  }), [apiTotals])

  const totalTransactionsCount = filteredTransactions.length

  const getTableHeight = useCallback(() => 'calc(100vh - 590px)', [])

  const checkIfTablet = useCallback(() => {
    setIsTablet(window.matchMedia('(min-width: 600px) and (max-width: 1200px)').matches)
  }, [])

  const isCreatedByCurrentUser = useCallback((record) => {
    if (!currentUser) return false

    const emailMatch = record.createdByEmail?.toLowerCase() === currentUser.username?.toLowerCase()
    const nameMatch = record.createdBy?.toLowerCase() === currentUser.name?.toLowerCase()

    return emailMatch || nameMatch
  }, [currentUser])

  const getCurrentUser = useCallback(async () => {
    try {
      const accounts = instance.getAllAccounts()
      if (accounts?.length > 0) {
        setCurrentUser({
          username: accounts[0].username,
          name: accounts[0].name
        })
      }
    } catch (error) {
      console.error('Error getting current user:', error)
    }
  }, [instance])

  const handleClearAllFilters = useCallback(() => {
    setTableFilters({})
    setFilteredTransactions([...transactions])
    setFilteredTotals({
      cashIn: totalTransactions.cashIn,
      cashOut: totalTransactions.cashOut,
      runningBalance: totalTransactions.runningBalance
    })
  }, [transactions, totalTransactions])

  const handleTableChange = useCallback((pagination, filters) => {
    setTableFilters(filters)

    const areAllFiltersEmpty = Object.values(filters).every(
      filter => !filter || filter.length === 0
    )

    const filteredData = areAllFiltersEmpty ? transactions : [...transactions]
    const reindexedData = filteredData.map((item, index) => ({
      ...item,
      srNo: index + 1
    }))

    setFilteredTransactions(reindexedData)

    const newFilteredTotals = {
      cashIn: filteredData.reduce((total, record) => total + (record.cashIn || 0), 0),
      cashOut: filteredData.reduce((total, record) => total + (record.cashOut || 0), 0),
      runningBalance: apiTotals.systemCOH
    }

    setFilteredTotals(newFilteredTotals)
  }, [transactions, apiTotals.systemCOH])

  const mapTransactionData = useCallback((transactionsArray) => {
    return transactionsArray.map((item, index) => {
      const isTransactionIn = item.cashFlowType === 'cash-in'
      const invoiceDateTime = dayjs.utc(item.invoiceDateTime)
      const createdAtDateTime = dayjs.utc(item.createdAt)

      const createdByName = item.createdByUser?.userName || item.createdBy || 'System'
      const createdByEmail = item.createdByUser?.emailId || item.createdByEmail || ''

      const fullDateTime = `${invoiceDateTime.format('YYYY-MM-DD')}T${invoiceDateTime.format('HH:mm:ss')}`

      return {
        key: index + 1,
        srNo: index + 1,
        transactionId: item.transactionId || '',
        invoiceNumber: item.invoiceNumber || '',
        date: invoiceDateTime.format('YYYY-MM-DD'),
        time: invoiceDateTime.format('h:mm A'),
        dateTime: fullDateTime,
        rawDateTime: invoiceDateTime.toDate(),
        createdAt: createdAtDateTime.format('YYYY-MM-DD h:mm A'),
        createdAtRaw: createdAtDateTime.toDate(),
        description: item.description || '',
        expenseType: item.transactionUsage || '',
        createdBy: createdByName,
        createdByEmail: createdByEmail,
        cashOut: !isTransactionIn ? item.amount : null,
        cashIn: isTransactionIn ? item.amount : null,
        hasAttachment: item.attachments?.length > 0,
        attachments: item.attachments?.map((att, idx) => ({
          id: idx + 1,
          name: att.filename || `File ${idx + 1}`,
          url: att.s3Location || '',
          type: att.filename?.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image',
          attachmentId: att._id || att.attachmentId || ''
        })) || [],
        runningBalance: item.runningBalance || 0,
        ...item
      }
    })
  }, [])

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true)

      const params = {
        month: selectedDate.format('MM'),
        year: selectedDate.format('YYYY'),
        txnType: 'pettyCashTxn',
        status: 'active'
      }

      const response = await fetchTransactions(instance, params)

      if (response.isDisclaimer) {
        onDataLoaded?.(null, response.rawResponse)
        setTransactions([])
        setFilteredTransactions([])
        setTableFilters({})
        setApiTotals(EMPTY_TOTALS)
        onBalanceUpdate?.('0')
        onTransactionsLoaded?.()
        return
      }

      const apiData = response.data

      if (apiData?.pettyCashTransactions) {
        const transactionsArray = apiData.pettyCashTransactions.records || [apiData.pettyCashTransactions]
        const metrics = apiData.pettyCashTransactions.metrics || {}

        setApiTotals({
          cashIn: metrics.totalDeposit || 0,
          cashOut: metrics.totalExpenses || 0,
          systemCOH: metrics.systemCOH || 0,
          openingBalance: metrics.openingBalance || 0
        })

        const mappedTransactions = mapTransactionData(transactionsArray)

        setExpenseDepositTypes(getUniqueValues(mappedTransactions, 'expenseType'))
        setCreatorNames(getUniqueValues(mappedTransactions, 'createdBy'))

        setTransactions(mappedTransactions)
        setFilteredTransactions(mappedTransactions)
        setTableFilters({})

        onDataLoaded?.({
          transactions: mappedTransactions,
          metrics,
          isCurrentMonthClosed: apiData.isCurrentMonthClosed || false
        }, response.rawResponse)

        onBalanceUpdate?.(metrics.systemCOH.toString())
        onTransactionsLoaded?.()
      } else {
        const emptyData = {
          transactions: [],
          metrics: {
            totalDeposit: 0, totalExpenses: 0, systemCOH: 0, openingBalance: 0
          }
        }
        setTransactions([])
        setFilteredTransactions([])
        setTableFilters({})

        onDataLoaded?.(emptyData, response.rawResponse)
        onBalanceUpdate?.('0')
        onTransactionsLoaded?.()
      }
    } catch (error) {
      console.error('Error loading transactions:', error)

      if (error.response?.data?.message?.toLowerCase().includes('previous month cash balance is not closed')) {
        onDataLoaded?.(null, error.response.data)
        setTransactions([])
        setFilteredTransactions([])
        setTableFilters({})
        setApiTotals(EMPTY_TOTALS)
        onBalanceUpdate?.('0')
        onTransactionsLoaded?.()
        return
      }

      message.error('Failed to load transactions. Please try again later.')
      setTransactions([])
      setFilteredTransactions([])
      setTableFilters({})
      onTransactionsLoaded?.()
    } finally {
      setLoading(false)
    }
  }, [selectedDate, instance, onDataLoaded, onBalanceUpdate, mapTransactionData, onTransactionsLoaded])

  const handleDelete = useCallback(async (record) => {
    if (isCurrentDatePrevMonth) return

    if (!isCreatedByCurrentUser(record)) {
      message.error('You can only delete transactions you created')
      return
    }

    try {
      const id = record._id
      if (!id) throw new Error('Missing _id in transaction record')

      setActionLoading(prev => ({ ...prev, delete: record.key }))

      const response = await deleteTransaction(id, instance)

      onTransactionDeleted?.(record)

      const updatedTransactions = transactions
        .filter(item => item.key !== record.key)
        .map((transaction, index) => ({
          ...transaction,
          srNo: index + 1,
          key: index + 1
        }))

      setTransactions(updatedTransactions)
      setFilteredTransactions(updatedTransactions)

      loadTransactions()
      message.success(response?.message || 'Transaction deleted successfully')
    } catch (error) {
      console.error('Error deleting transaction:', error)
      message.error(error.response?.data?.message || error.message || 'Failed to delete transaction')
    } finally {
      setActionLoading(prev => ({ ...prev, delete: null }))
    }
  }, [isCurrentDatePrevMonth, isCreatedByCurrentUser, transactions, instance, onTransactionDeleted, loadTransactions])

  const handleViewAttachment = async (record) => {
    try {
      setActionLoading(prev => ({
        ...prev,
        previewAttachment: {},
        downloadAttachment: {},
        deleteAttachment: {},
        uploadAttachment: false
      }))

      setCurrentAttachment(record)
      setFileList([])

      if (record.transactionId) {
        if (record.attachments && record.attachments.length > 0) {
          const updatedRecord = {
            ...record,
            attachments: record.attachments.map((att, idx) => ({
              ...att,
              id: att.id || idx + 1,
              name: att.name || att.filename || `Attachment ${idx + 1}`
            }))
          }
          setCurrentAttachment(updatedRecord)
          setIsAttachmentModalVisible(true)
          return
        }

        const attachmentsData = await fetchTransactionAttachments(record.transactionId)

        if (attachmentsData?.data) {
          const updatedRecord = {
            ...record,
            attachments: attachmentsData.data.map((att, idx) => ({
              id: idx + 1,
              name: att.filename || `File ${idx + 1}`,
              url: att.s3Location || att.fileUrl || '',
              type: att.filename?.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image',
              attachmentId: att._id || att.attachmentId || ''
            })),
            hasAttachment: attachmentsData.data.length > 0
          }

          setCurrentAttachment(updatedRecord)

          const updatedTransactions = transactions.map(transaction =>
            transaction.key === record.key ? updatedRecord : transaction
          )

          setTransactions(updatedTransactions)
          setFilteredTransactions(updatedTransactions.filter(t =>
            filteredTransactions.some(ft => ft.key === t.key)
          ))
        }
      }

      setIsAttachmentModalVisible(true)
    } catch (error) {
      console.error('Error fetching attachments:', error)
      message.error(error.response?.data?.message || error.message || 'Failed to load attachments')
    }
  }

  const handleUploadFile = async (file) => {
    if (isCurrentDatePrevMonth) {
      message.error('Cannot upload attachments for previous months')
      return false
    }

    if (!currentAttachment) {
      message.error('No transaction selected')
      return Upload.LIST_IGNORE
    }

    if (!isCreatedByCurrentUser(currentAttachment)) {
      message.error('You can only upload attachments to transactions you created')
      return Upload.LIST_IGNORE
    }

    const isImage = file.type.startsWith('image/')
    const isPdf = file.type === 'application/pdf'

    if (!isImage && !isPdf) {
      message.error(`${file.name} is not an image or PDF file`)
      return Upload.LIST_IGNORE
    }

    try {
      setActionLoading(prev => ({ ...prev, uploadAttachment: true }))

      const response = await handleAttachments(
        currentAttachment._id,
        'update',
        null,
        file,
        instance
      )

      if (response) {
        const maxId = currentAttachment.attachments?.length > 0
          ? Math.max(...currentAttachment.attachments.map(a => a.id))
          : 0

        const newAttachment = {
          id: maxId + 1,
          name: file.name,
          url: response.data?.s3Location || response.data?.fileUrl || URL.createObjectURL(file),
          type: isPdf ? 'pdf' : 'image',
          attachmentId: response.data?._id || response.data?.attachmentId || ''
        }

        const updatedCurrentAttachment = {
          ...currentAttachment,
          attachments: [...(currentAttachment.attachments || []), newAttachment],
          hasAttachment: true
        }

        setCurrentAttachment(updatedCurrentAttachment)

        const updatedTransactions = transactions.map(transaction =>
          transaction.key === currentAttachment.key
            ? { ...transaction, attachments: [...(transaction.attachments || []), newAttachment], hasAttachment: true }
            : transaction
        )

        setTransactions(updatedTransactions)
        setFilteredTransactions(updatedTransactions)

        message.success(response?.message || 'File uploaded successfully')
        setIsAttachmentModalVisible(false)

        setTimeout(() => loadTransactions(), 1000)
      }

      return false
    } catch (error) {
      console.error('Error uploading file:', error)
      
      // Check for file size errors - either 413 status, userFriendlyMessage, or network error on file upload
       if (error.response) {
              const errorMessage = error.response.data?.message || error.response.data?.error || error.response.statusText
              message.error(`Failed to upload file: ${errorMessage}`)
            } else if (error.request) {
              message.error('Content is too large. Please reduce the file size and try again.')
            } else {
              message.error('Failed to upload file: ' + (error.message || 'Unknown error'))
            }
      return Upload.LIST_IGNORE
    } finally {
      setActionLoading(prev => ({ ...prev, uploadAttachment: false }))
    }
  }

  const handlePreviewDocument = async (doc) => {
    try {
      setActionLoading(prev => ({
        ...prev,
        previewAttachment: { ...prev.previewAttachment, [doc.id]: true }
      }))

      if (doc.originFileObj) {
        const url = URL.createObjectURL(doc.originFileObj)
        window.open(url, '_blank')
        URL.revokeObjectURL(url)
        return
      }

      const attachmentS3Path = getAttachmentS3Path(doc, currentAttachment?.transactionId)
      if (!attachmentS3Path) {
        throw new Error(`Could not determine attachment path for ${doc.name}`)
      }

      const presignedUrl = await generatePresignedUrl(attachmentS3Path, instance)
      if (!presignedUrl) {
        throw new Error(`Failed to generate preview URL for ${doc.name}`)
      }

      window.open(presignedUrl, '_blank')
    } catch (error) {
      console.error(`Error previewing document ${doc.name}:`, error)
      message.error(`Failed to preview ${doc.name}. Please try again.`)
    } finally {
      setActionLoading(prev => {
        const previewAttachmentCopy = { ...prev.previewAttachment }
        delete previewAttachmentCopy[doc.id]
        return { ...prev, previewAttachment: previewAttachmentCopy }
      })
    }
  }

  const handleDownloadDocument = async (doc) => {
    try {
      setActionLoading(prev => ({
        ...prev,
        downloadAttachment: { ...prev.downloadAttachment, [doc.id]: true }
      }))

      if (doc.originFileObj) {
        const fileUrl = URL.createObjectURL(doc.originFileObj)
        window.open(fileUrl, '_blank')
        setTimeout(() => URL.revokeObjectURL(fileUrl), 1000)
        return
      }

      const attachmentS3Path = getAttachmentS3Path(doc, currentAttachment?.transactionId)
      if (!attachmentS3Path) {
        throw new Error(`Could not determine attachment path for ${doc.name}`)
      }

      const presignedUrl = await generatePresignedUrl(attachmentS3Path, instance)
      if (!presignedUrl) {
        throw new Error(`Failed to generate URL for ${doc.name}`)
      }

      window.open(presignedUrl, '_blank')
    } catch (error) {
      console.error(`Error opening document ${doc.name}:`, error)
      message.error(`Failed to open ${doc.name}. Please try again.`)
    } finally {
      setActionLoading(prev => {
        const downloadAttachmentCopy = { ...prev.downloadAttachment }
        delete downloadAttachmentCopy[doc.id]
        return { ...prev, downloadAttachment: downloadAttachmentCopy }
      })
    }
  }

  const handleDeleteDocument = async (documentId, attachmentId) => {
    if (isCurrentDatePrevMonth) {
      message.error('Cannot delete attachments from previous months')
      return
    }

    if (!currentAttachment || !isCreatedByCurrentUser(currentAttachment)) {
      message.error('You can only delete attachments from transactions you created')
      return
    }

    setActionLoading(prev => ({
      ...prev,
      deleteAttachment: { ...prev.deleteAttachment, [documentId]: true }
    }))

    try {
      if (!currentAttachment.attachments) {
        throw new Error('Attachment data not found')
      }

      const attachmentToDelete = currentAttachment.attachments.find(att => att.id === documentId)
      if (!attachmentToDelete) {
        throw new Error('Attachment not found')
      }

      const attachmentIdToDelete = attachmentId || attachmentToDelete.attachmentId || attachmentToDelete._id
      if (!attachmentIdToDelete) {
        throw new Error('Could not find attachment ID')
      }

      const response = await handleAttachments(
        currentAttachment._id,
        'delete',
        attachmentIdToDelete,
        null,
        instance
      )

      const updatedTransactions = transactions.map(transaction => {
        if (transaction.key === currentAttachment.key && transaction.attachments) {
          const updatedAttachments = transaction.attachments.filter(att => att.id !== documentId)
          return {
            ...transaction,
            attachments: updatedAttachments,
            hasAttachment: updatedAttachments.length > 0
          }
        }
        return transaction
      })

      const updatedCurrentAttachment = {
        ...currentAttachment,
        attachments: currentAttachment.attachments.filter(att => att.id !== documentId),
        hasAttachment: currentAttachment.attachments.filter(att => att.id !== documentId).length > 0
      }

      setCurrentAttachment(updatedCurrentAttachment)
      setTransactions(updatedTransactions)
      setFilteredTransactions(updatedTransactions.filter(t =>
        filteredTransactions.some(ft => ft.key === t.key)
      ))

      message.success(response?.message || 'Document deleted successfully')
      setTimeout(() => loadTransactions(), 1000)
    } catch (error) {
      console.error('Error deleting document:', error)
      message.error(error.response?.data?.message || error.message || 'Failed to delete document')
    } finally {
      setActionLoading(prev => {
        const deleteAttachmentCopy = { ...prev.deleteAttachment }
        delete deleteAttachmentCopy[documentId]
        return { ...prev, deleteAttachment: deleteAttachmentCopy }
      })
    }
  }

  useEffect(() => {
    if (transactionSubmitted) {
      loadTransactions()
      onTransactionRefreshed?.()
    }
  }, [transactionSubmitted, onTransactionRefreshed, loadTransactions])

  useEffect(() => {
    getCurrentUser()
  }, [getCurrentUser])

  useEffect(() => {
    checkIfTablet()
    window.addEventListener('resize', checkIfTablet)
    return () => window.removeEventListener('resize', checkIfTablet)
  }, [checkIfTablet])

  useEffect(() => {
    loadTransactions()
  }, [loadTransactions])

  useImperativeHandle(ref, () => ({
    getExportData: () => ({
      transactions: filteredTransactions,
      totalTransactions: filteredTransactions.length === transactions.length ? totalTransactions : filteredTotals,
      totalTransactionsCount: filteredTransactions.length,
      apiMetrics: apiTotals
    }),
    refreshTransactions: loadTransactions,
    getCurrentBalance: () => apiTotals.systemCOH || 0,
    hasFiltersApplied: () => filteredTransactions.length !== transactions.length,
    clearAllFilters: handleClearAllFilters
  }), [filteredTransactions, transactions, totalTransactions, filteredTotals, apiTotals, loadTransactions, handleClearAllFilters])

  const renderAmountCell = useCallback((text, record, type) => {
    if (type === 'cashOut' && text) {
      return <Text style={{ color: '#d9363e' }}>{formatCurrency(text)}</Text>
    }

    if (type === 'cashIn' && text) {
      return <Text style={{ color: '#52c41a' }}>{formatCurrency(text)}</Text>
    }

    if (type === 'runningBalance') {
      return <Text>{formatCurrency(text)}</Text>
    }

    return null
  }, [])

  const renderActionColumn = useCallback((text, record) => {
    const isUserCreator = isCreatedByCurrentUser(record)

    const actionButtons = [
      {
        key: 'attachment',
        icon: <FileTextOutlined />,
        tooltip: 'View/Edit Attachment',
        className: 'approved-attachment',
        onClick: () => handleViewAttachment(record)
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        tooltip: isCurrentDatePrevMonth
          ? 'Cannot delete transactions from previous months'
          : !isUserCreator
            ? 'You can only delete transactions you created'
            : 'Delete',
        danger: true,
        loading: actionLoading.delete === record.key,
        disabled: isCurrentDatePrevMonth || !isUserCreator,
        popconfirm: {
          title: 'Are you sure you want to delete this transaction?',
          onConfirm: () => handleDelete(record)
        }
      }
    ]

    return (
      <div className="action-cell">
        {actionButtons.map(button => {
          const ButtonComponent = button.popconfirm ? (
            <Popconfirm {...button.popconfirm} disabled={button.disabled}>
              <Button
                type="text"
                icon={button.icon}
                size="small"
                loading={button.loading}
                disabled={button.disabled}
                danger={button.danger}
                className={button.className}
              />
            </Popconfirm>
          ) : (
            <Button
              type="text"
              icon={button.icon}
              size="small"
              loading={button.loading}
              disabled={button.disabled}
              danger={button.danger}
              className={button.className}
              onClick={button.onClick}
            />
          )

          return (
            <Tooltip key={button.key} title={button.tooltip}>
              {ButtonComponent}
            </Tooltip>
          )
        })}
      </div>
    )
  }, [isCreatedByCurrentUser, isCurrentDatePrevMonth, actionLoading, handleViewAttachment, handleDelete])

  const renderClearFiltersButton = useCallback(() => {
    const hasFilters = Object.keys(tableFilters).some(key =>
      tableFilters[key]?.length > 0
    )

    return hasFilters ? (
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Button
          onClick={handleClearAllFilters}
          icon={<FilterOutlined />}
          type="default"
          size="small"
        >
          Clear All Filters
        </Button>
      </div>
    ) : null
  }, [tableFilters, handleClearAllFilters])

  const allColumns = useMemo(() => [
    {
      title: 'Sr. No.',
      dataIndex: 'srNo',
      key: 'srNo',
      width: 80,
      fixed: isTablet ? false : 'left',
      className: 'column-sr-no',
      align: 'center',
      render: (text, record, index) => index + 1
    },
    {
      title: 'Transaction ID',
      dataIndex: 'transactionId',
      key: 'transactionId',
      fixed: isTablet ? false : 'left',
      width: 150,
      filteredValue: tableFilters.transactionId || null,
      ...getColumnSearchProps('transactionId', 'Transaction ID', Input, Button, SearchOutlined),
      render: (text) => (
        <div className="transaction-id-cell">
          <span>{text}</span>
        </div>
      )
    },
    {
      title: 'Invoice Number',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      width: 150,
      filteredValue: tableFilters.invoiceNumber || null,
      ...getColumnSearchProps('invoiceNumber', 'Invoice Number', Input, Button, SearchOutlined),
      render: (text) => <div className="invoice-cell"><span>{text}</span></div>
    },
    {
      title: 'Invoice Date and Time',
      dataIndex: 'date',
      key: 'date',
      width: 200,
      filteredValue: tableFilters.date || null,
      ...getDateFilterProps(RangePicker, Button, SearchOutlined, selectedDate),
      render: (text, record) => `${record.date} ${record.time}`
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 180,
      ellipsis: true,
      filteredValue: tableFilters.description || null,
      ...getColumnSearchProps('description', 'Description', Input, Button, SearchOutlined),
      render: (text) => (
        <Tooltip title={text} placement="topLeft">
          <div className="description-cell" style={{
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {text}
          </div>
        </Tooltip>
      )
    },
    {
      title: 'Expense/Deposit Type',
      dataIndex: 'expenseType',
      key: 'expenseType',
      width: 220,
      filteredValue: tableFilters.expenseType || null,
      ...getCustomFilterWithSearch('expenseType', expenseDepositTypes, Input, Button, SearchOutlined, FilterOutlined, Checkbox)
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 150,
      filteredValue: tableFilters.createdBy || null,
      ...getCustomFilterWithSearch('createdBy', creatorNames, Input, Button, SearchOutlined, FilterOutlined, Checkbox)
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      filteredValue: tableFilters.createdAt || null,
      ...getDateFilterProps(RangePicker, Button, SearchOutlined, selectedDate),
      render: (text, record) => dayjs(record.createdAtRaw).format('YYYY-MM-DD h:mm A')
    },
    {
      title: 'Cash Out',
      dataIndex: 'cashOut',
      key: 'cashOut',
      width: 120,
      fixed: 'right',
      className: 'column-cash-out',
      align: 'right',
      render: (text, record) => renderAmountCell(text, record, 'cashOut')
    },
    {
      title: 'Cash In',
      dataIndex: 'cashIn',
      key: 'cashIn',
      width: 120,
      fixed: 'right',
      className: 'column-cash-in',
      align: 'right',
      render: (text, record) => renderAmountCell(text, record, 'cashIn')
    },
    {
      title: 'Running Balance',
      dataIndex: 'runningBalance',
      key: 'runningBalance',
      width: 150,
      fixed: 'right',
      className: 'column-running-balance',
      align: 'right',
      render: (text, record) => renderAmountCell(text, record, 'runningBalance')
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: renderActionColumn
    }
  ], [
    isTablet, tableFilters, selectedDate, expenseDepositTypes, creatorNames,
    renderAmountCell, renderActionColumn
  ])

  const filteredColumns = useMemo(() =>
    visibleColumns.length > 0
      ? allColumns.filter(column => visibleColumns.includes(column.key))
      : allColumns
    , [allColumns, visibleColumns])

  const renderTotalRow = useCallback((label, value, className = '') => (
    <div className="total-row">
      <span>{label}</span>
      <div className="total-value-container">
        <span className={`total-value ${className}`}>
          {formatCurrency(value)}
        </span>
        {label === 'Running Balance' && isCurrentDatePrevMonth && (
          <span style={{ fontSize: '12px', color: 'rgb(3 46 84)' }}>
            Closing balance
          </span>
        )}
      </div>
    </div>
  ), [isCurrentDatePrevMonth])

  const currentTotals = useMemo(() =>
    filteredTransactions.length === transactions.length ? totalTransactions : filteredTotals
    , [filteredTransactions.length, transactions.length, totalTransactions, filteredTotals])

  if (loading) {
    return (
      <div className="spinner-container">
        <Spinner size="large" />
      </div>
    )
  }

  return (
    <ConfigProvider modal={{ okText: 'Apply', cancelText: 'Close' }}>
      <div className={`table-container ${isTablet ? 'tablet-view' : ''}`}>
        <div className="table-responsive">
          {renderClearFiltersButton()}
          <Table
            className={`transactions-table ${isTablet ? 'tablet-mode' : ''}`}
            columns={getResponsiveTableProps(filteredColumns).columns}
            dataSource={filteredTransactions}
            onChange={handleTableChange}
            pagination={false}
            rowKey="key"
            scroll={{
              ...getResponsiveTableProps(filteredColumns).scroll,
              y: getTableHeight()
            }}
            sticky={getResponsiveTableProps(filteredColumns).sticky}
          />

          <div className="transactions-footer">
            <div className="footer-content">
              <div className="footer-left">
                <span className="transaction-count">
                  {totalTransactionsCount} Transactions
                </span>
              </div>
              <div className="footer-right">
                <div className="footer-columns">
                  <div className="footer-spacer-before"></div>

                  <div className="summary-amount cash-out">
                    <Text style={{ color: '#d9363e' }}>
                      {formatCurrency(currentTotals.cashOut)}
                    </Text>
                  </div>

                  {isCashInVisible && (
                    <div className="summary-amount cash-in">
                      <Text style={{ color: '#52c41a' }}>
                        {formatCurrency(currentTotals.cashIn)}
                      </Text>
                    </div>
                  )}

                  {isRunningBalanceVisible && (
                    <div className="summary-amount balance" style={{ width: '150px', textAlign: 'right' }}>
                      <Text style={{
                        display: 'inline-block',
                        width: '100%',
                        textAlign: 'right',
                        color: currentTotals.runningBalance < 0 ? '#d9363e' : 'inherit'
                      }}>
                        {formatCurrency(currentTotals.runningBalance)}
                      </Text>
                    </div>
                  )}
                  <div className="footer-spacer-after"></div>
                </div>
              </div>
            </div>

            <div className="mobile-totals">
              {renderTotalRow('Cash Out', currentTotals.cashOut, 'cash-out')}
              {isCashInVisible && renderTotalRow('Cash In', currentTotals.cashIn, 'cash-in')}
              {isRunningBalanceVisible && renderTotalRow(
                'Running Balance',
                currentTotals.runningBalance,
                currentTotals.runningBalance < 0 ? 'negative' : ''
              )}
            </div>
          </div>
        </div>
      </div>

      <AttachmentModal
        visible={isAttachmentModalVisible}
        onClose={() => setIsAttachmentModalVisible(false)}
        transaction={currentAttachment}
        fileList={fileList}
        setFileList={setFileList}
        actionLoading={actionLoading}
        isCurrentDatePrevMonth={isCurrentDatePrevMonth}
        currentUser={currentUser}
        handleUploadFile={handleUploadFile}
        handlePreviewDocument={handlePreviewDocument}
        handleDownloadDocument={handleDownloadDocument}
        handleDeleteDocument={handleDeleteDocument}
      />
    </ConfigProvider>
  )
})

export default Transactions